import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, Briefcase, Pencil } from "lucide-react";
import { personsApi } from "@/api/counterparties";
import { ApiError } from "@/api/client";
import type { PersonDetailDto } from "@/types/counterparties";
import { COUNTERPARTY_PERMISSIONS } from "@/types/counterparties";
import {
  Badge,
  Button,
  DetailField as Field,
  DetailFieldList,
  RECORD_CARD_MIN_WIDTH,
  RECORD_RAIL_WIDTH,
  ResponsiveGrid,
  ResponsiveSplit,
} from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { useAuth } from "@/hooks/useAuth";
import { useDialogs } from "@/hooks/useDialogs";
import { useToast } from "@/components/ui/Toast";
import { CounterpartyStatusBadge } from "@/components/counterparties/StatusBadge";
import { RequestDocumentsButton } from "@/components/Collection/RequestDocumentsButton";
import { CounterpartyDealsTab } from "@/components/counterparties/CounterpartyDealsTab";
import { ActiveDealsBlockedDialog } from "@/components/counterparties/ActiveDealsBlockedDialog";
import { DocumentPanel } from "@/pages/documents/components/DocumentPanel";
import { TaskListMini } from "@/pages/tasks/components/TaskListMini";
import { AppointmentListMini } from "@/pages/calendar/components/AppointmentListMini";
import { CommunicationsTabPanel } from "@/pages/communications/components/CommunicationsTabPanel";
import { NotesTabPanel } from "@/pages/notes/components/NotesTabPanel";
import { ActivityTabPanel } from "@/pages/activity/components/ActivityTabPanel";
import { RelatedConversationsPanel } from "@/components/messaging/RelatedConversationsPanel";
import { OverviewActivityPreview } from "@/components/activity/OverviewActivityPreview";
import { useOpenTaskCountByRelated } from "@/pages/tasks/hooks/useWorkTasks";
import { useCommunicationsRecentCount } from "@/pages/communications/hooks/useCommunications";
import { useNotesRecentCount } from "@/pages/notes/hooks/useNotes";
import { useActivityRecentCount } from "@/pages/activity/hooks/useActivityByOwner";
import {
  CROSS_CUTTING_TAB_ICONS,
  EntityTabBar,
  type EntityTabItem,
} from "@/components/layout/EntityTabBar";
import { useDateFormatter } from "@/utils/datetime";
import { displayEmail, NO_EMAIL_ON_FILE } from "@/design-system/lib/format";
import { invalidateCounterpartyReads, personKeys } from "../counterpartyKeys";

const PERMS = COUNTERPARTY_PERMISSIONS.Person;

// Layout Compliance v1.0 PR 9 — Person is one of three counterparty
// detail pages migrated to the canonical layout. Per Layout Standard
// §3 the entity-specific Deals tab keeps its position; the cross-cutting
// block (Documents / Tasks / Calendar / Messages / Communications / Notes /
// Activity) is appended in canonical order.
type TabId =
  | "overview"
  | "deals"
  | "documents"
  | "tasks"
  | "calendar"
  | "messages"
  | "communications"
  | "notes"
  | "activity";

const TABS: readonly TabId[] = [
  "overview",
  "deals",
  "documents",
  "tasks",
  "calendar",
  "messages",
  "communications",
  "notes",
  "activity",
] as const;

const TAB_ITEMS: ReadonlyArray<EntityTabItem<TabId>> = [
  { key: "overview", label: "Overview", icon: CROSS_CUTTING_TAB_ICONS.overview },
  { key: "deals", label: "Deals", icon: Briefcase },
  { key: "documents", label: "Documents", icon: CROSS_CUTTING_TAB_ICONS.documents },
  { key: "tasks", label: "Tasks", icon: CROSS_CUTTING_TAB_ICONS.tasks },
  { key: "calendar", label: "Calendar", icon: CROSS_CUTTING_TAB_ICONS.calendar },
  { key: "messages", label: "Messages", icon: CROSS_CUTTING_TAB_ICONS.messages },
  { key: "communications", label: "Communications", icon: CROSS_CUTTING_TAB_ICONS.communications },
  { key: "notes", label: "Notes", icon: CROSS_CUTTING_TAB_ICONS.notes },
  { key: "activity", label: "Activity", icon: CROSS_CUTTING_TAB_ICONS.activity },
] as const;

function isTabId(value: string | null): value is TabId {
  return value !== null && (TABS as readonly string[]).includes(value);
}

export function PersonDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dialogs = useDialogs();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const { date } = useDateFormatter();
  const [archiveBlocked, setArchiveBlocked] = useState(false);

  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const tab: TabId = isTabId(tabParam) ? tabParam : "overview";

  const setTab = useCallback(
    (next: TabId) => {
      const updated = new URLSearchParams(params);
      if (next === "overview") updated.delete("tab");
      else updated.set("tab", next);
      setParams(updated, { replace: true });
    },
    [params, setParams],
  );

  // Numeric shortcuts: 1-9 jump tabs when focus is not in an input.
  useEffect(() => {
    function isTypingTarget(t: EventTarget | null): boolean {
      if (!(t instanceof HTMLElement)) return false;
      if (t.isContentEditable) return true;
      const tag = t.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
    }
    function handler(e: KeyboardEvent) {
      if (isTypingTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const idx = Number(e.key);
      if (Number.isInteger(idx) && idx >= 1 && idx <= TABS.length) {
        e.preventDefault();
        setTab(TABS[idx - 1]);
      }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [setTab]);

  const canUpdate = hasPermission(PERMS.Update);
  const canDelete = hasPermission(PERMS.Delete);

  const personQuery = useQuery({
    queryKey: personKeys.detail(id),
    queryFn: () => personsApi.get(id!),
    enabled: !!id,
  });

  useBreadcrumbs(
    personQuery.data
      ? [
          { label: "Persons", href: "/counterparties/persons" },
          { label: personQuery.data.displayName },
        ]
      : null,
  );

  const archive = useMutation({
    mutationFn: () => personsApi.archive(id!),
    onSuccess: () => {
      invalidateCounterpartyReads(queryClient, "person");
      toast.success("Person archived.");
      navigate("/counterparties/persons");
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) {
        setArchiveBlocked(true);
      } else {
        toast.error(e instanceof Error ? e.message : "Archive failed.");
      }
    },
  });

  // Tab badges (Layout Standard §5) sourced from central by-owner counters.
  const openTaskCountQuery = useOpenTaskCountByRelated("Person", id ?? undefined);
  const openTaskCount = openTaskCountQuery.data ?? 0;
  const commsRecent = useCommunicationsRecentCount("person", id ?? "", 30);
  const notesRecent = useNotesRecentCount("person", id ?? "", 7);
  const activityRecent = useActivityRecentCount("person", id ?? "", 7);

  if (personQuery.isLoading) {
    return <div className="text-caption uppercase tracking-wide text-gray-mid">Loading…</div>;
  }
  if (personQuery.isError || !personQuery.data) {
    return (
      <div className="text-small text-status-error">
        {(personQuery.error as Error)?.message ?? "Person not found."}
      </div>
    );
  }

  const person = personQuery.data;

  const onArchive = async () => {
    const ok = await dialogs.confirm({
      title: `Archive ${person.displayName}?`,
      message:
        "This counterparty will no longer appear in active lists. Existing deal references are preserved.",
      confirmLabel: "Archive",
      destructive: true,
    });
    if (!ok) return;
    archive.mutate();
  };

  const tabBadge = (key: TabId): string | number | null => {
    switch (key) {
      case "tasks":
        return openTaskCount > 0 ? openTaskCount : null;
      case "communications":
        return commsRecent.data && commsRecent.data > 0 ? commsRecent.data : null;
      case "notes":
        return notesRecent.data && notesRecent.data > 0 ? notesRecent.data : null;
      case "activity":
        return activityRecent.data && activityRecent.data > 0 ? activityRecent.data : null;
      default:
        return null;
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Counterparties · Person"
        title={person.displayName}
        actions={
          <>
            <RequestDocumentsButton
              ownerEntityType="Person"
              ownerEntityId={person.id}
              ownerDisplayName={person.displayName}
              size="sm"
            />
            {canUpdate && (
              <Button size="sm" variant="secondary" onClick={() => navigate(`/counterparties/persons/${person.id}/edit`)}>
                <Pencil size={14} aria-hidden /> Edit
              </Button>
            )}
            {canDelete && person.status === "Active" && (
              <Button size="sm" variant="destructive" onClick={onArchive} disabled={archive.isPending}>
                <Archive size={14} aria-hidden /> Archive
              </Button>
            )}
          </>
        }
      />

      <div className="flex items-center gap-space-3 -mt-space-3 mb-space-4">
        <CounterpartyStatusBadge status={person.status} />
        <span className="text-caption text-gray-mid">
          Created {date(person.createdAt)}
          {person.modifiedAt ? ` · Modified ${date(person.modifiedAt)}` : ""}
        </span>
      </div>

      {/* Layout Standard §6.1 — sticky tab bar below the entity header. */}
      <EntityTabBar
        ariaLabel="Person sections"
        testIdPrefix="person"
        items={TAB_ITEMS.map((it) => ({ ...it, badge: tabBadge(it.key) }))}
        value={tab}
        onChange={setTab}
      />

      <div className="pt-space-4">
        {tab === "overview" && (
          // Container-driven — Table & Tile Standard §12. A `lg:` breakpoint
          // asks the WINDOW how wide this box is, which is the one thing the
          // window cannot answer once the box is a tab body or a drawer.
          <ResponsiveSplit
            gap="md"
            mainMinWidth="34rem"
            railWidth={RECORD_RAIL_WIDTH}
            railGrows={false}
            main={<PersonOverview person={person} />}
            rail={
              <OverviewActivityPreview
                ownerType="person"
                ownerId={person.id}
                detailPath={`/counterparties/persons/${person.id}`}
              />
            }
          />
        )}
        {tab === "deals" && <CounterpartyDealsTab kind="Person" personId={person.id} />}
        {tab === "documents" && (
          <DocumentPanel ownerType="Person" ownerId={person.id} rootName={person.displayName} />
        )}
        {tab === "tasks" && (
          <TaskListMini
            relatedEntityType="Person"
            relatedEntityId={person.id}
            addButtonLabel="+ Add task linked to this person"
          />
        )}
        {tab === "calendar" && (
          <AppointmentListMini
            relatedEntityType="Person"
            relatedEntityId={person.id}
            entityLabel={person.displayName}
          />
        )}
        {tab === "messages" && (
          <RelatedConversationsPanel
            resourceType="person"
            resourceId={person.id}
            entityLabel={person.displayName}
          />
        )}
        {tab === "communications" && (
          <CommunicationsTabPanel
            ownerType="person"
            ownerId={person.id}
            ownerLabel={person.displayName}
          />
        )}
        {tab === "notes" && <NotesTabPanel ownerType="person" ownerId={person.id} />}
        {tab === "activity" && <ActivityTabPanel ownerType="person" ownerId={person.id} />}
      </div>

      {archiveBlocked && (
        <ActiveDealsBlockedDialog
          open
          displayName={person.displayName}
          onClose={() => setArchiveBlocked(false)}
        />
      )}
    </>
  );
}

function PersonOverview({ person }: { person: PersonDetailDto }) {
  return (
    <ResponsiveGrid cols={2} minColWidth={RECORD_CARD_MIN_WIDTH} gap="md">
      <Card title="Identity">
        <DetailFieldList>
          <Field label="Full name">{person.displayName}</Field>
          <Field label="Date of birth">{person.dateOfBirth ?? "—"}</Field>
          <Field label="SSN">
            <span className="font-mono">{person.ssnMasked ?? "—"}</span>
          </Field>
          <Field label="US citizenship">{person.usCitizen ?? "—"}</Field>
          <Field label="Marital status">{person.maritalStatus ?? "—"}</Field>
        </DetailFieldList>
      </Card>

      <Card title="Contact">
        <DetailFieldList>
          <Field label="Primary email">
            {displayEmail(person.primaryEmail) ?? (
              <span className="text-gray-mid">{NO_EMAIL_ON_FILE}</span>
            )}
          </Field>
          <Field label="Secondary email">{person.secondaryEmail ?? "—"}</Field>
          <Field label="Primary phone">
            {person.primaryPhone}
            {person.primaryPhoneType ? ` (${person.primaryPhoneType})` : ""}
          </Field>
          <Field label="Secondary phone">{person.secondaryPhone ?? "—"}</Field>
          <Field label="Preferred contact">{person.preferredContactMethod ?? "—"}</Field>
        </DetailFieldList>
      </Card>

      <Card title="Address">
        <AddressBlock label="Physical" address={person.address} />
        {person.mailingAddress && (
          <div className="mt-space-3">
            <AddressBlock label="Mailing" address={person.mailingAddress} />
          </div>
        )}
      </Card>

      <Card title="Role-specific">
        <DetailFieldList>
          <Field label="Industry experience (yrs)">{person.industryExperienceYears ?? "—"}</Field>
          <Field label="Estimated net worth">{formatMoney(person.estimatedNetWorth)}</Field>
          <Field label="Estimated liquidity">{formatMoney(person.estimatedLiquidity)}</Field>
          <Field label="Preliminary credit score">
            {person.preliminaryCreditScore ?? "—"}
            {person.preliminaryCreditScoreSource ? ` (${person.preliminaryCreditScoreSource})` : ""}
          </Field>
          <Field label="Equity injection source">{person.equityInjectionSource ?? "—"}</Field>
          <Field label="Accredited investor">
            {person.accreditedInvestorStatus ?? "—"}
            {person.accreditedInvestorBasis ? ` (${person.accreditedInvestorBasis})` : ""}
          </Field>
          <Field label="Tax filing status">{person.taxFilingStatus ?? "—"}</Field>
          <Field label="Original sale date">{person.originalSaleDate ?? "—"}</Field>
          <Field label="Original sale basis">{person.originalSaleBasisInfo ?? "—"}</Field>
        </DetailFieldList>
      </Card>

      <Card title="Banking">
        <DetailFieldList>
          <Field label="ACH name">{person.bankACHName ?? "—"}</Field>
          <Field label="Routing #"><span className="font-mono">{person.bankRoutingNumberMasked ?? "—"}</span></Field>
          <Field label="Account #"><span className="font-mono">{person.bankAccountNumberMasked ?? "—"}</span></Field>
          <Field label="Account type">{person.bankAccountType ?? "—"}</Field>
        </DetailFieldList>
      </Card>

      <Card title="Tags">
        <div className="flex flex-wrap gap-space-1">
          {person.tags.length === 0 ? (
            <span className="text-caption text-gray-mid">No tags</span>
          ) : (
            person.tags.map((t) => <Badge key={t} status="gold">{t}</Badge>)
          )}
        </div>
      </Card>
    </ResponsiveGrid>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white-pure border border-border-light rounded-lg p-space-4">
      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mb-space-3 m-0">
        {title}
      </h3>
      <div className="grid grid-cols-1 gap-space-2">{children}</div>
    </section>
  );
}

function AddressBlock({ label, address }: { label: string; address: { streetAddress1: string; streetAddress2?: string | null; city: string; state: string; zip: string; country: string } }) {
  return (
    <div className="text-small">
      <div className="text-caption uppercase tracking-wide text-gray-mid mb-space-1">{label}</div>
      <div className="text-onyx">{address.streetAddress1}</div>
      {address.streetAddress2 && <div className="text-onyx">{address.streetAddress2}</div>}
      <div className="text-onyx">
        {address.city}, {address.state} {address.zip}
      </div>
      <div className="text-gray-mid">{address.country}</div>
    </div>
  );
}

function formatMoney(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);
}
